import React from 'react';

interface FormFieldProps {
  label?: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  help?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function FormField({
  label,
  htmlFor,
  required,
  help,
  error,
  children,
  className = '',
}: FormFieldProps) {
  return (
    <div className={['ui-field', error ? 'ui-field-error' : '', className].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={htmlFor} className={`form-label${required ? ' required' : ''}`}>
          {label}
        </label>
      )}
      <div className="ui-field-control">{children}</div>
      {error ? (
        <p className="ui-field-message ui-field-message-error" role="alert">
          {error}
        </p>
      ) : help ? (
        <p className="ui-field-message ui-field-message-help">{help}</p>
      ) : null}
    </div>
  );
}

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  ({ className = '', invalid, ...rest }, ref) => (
    <input
      ref={ref}
      className={['ui-input', invalid ? 'ui-input-invalid' : '', className].filter(Boolean).join(' ')}
      {...rest}
    />
  ),
);
TextInput.displayName = 'TextInput';

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className = '', invalid, rows = 3, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={['ui-input', 'ui-textarea', invalid ? 'ui-input-invalid' : '', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    />
  ),
);
TextArea.displayName = 'TextArea';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className = '', invalid, children, ...rest }, ref) => (
    <select
      ref={ref}
      className={['ui-input', 'ui-select', invalid ? 'ui-input-invalid' : '', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';

export default FormField;
